<template>
  <div class="page-container">
    <div class="yn-biz-header">
      <span class="yn-biz-title">合同管理</span>
      <div class="header-actions">
        <el-button type="primary" size="mini" @click="openCreate">新建合同</el-button>
      </div>
    </div>

    <el-table :data="list" v-loading="loading" stripe border size="small">
      <el-table-column prop="contract_no" label="合同编号" width="160" />
      <el-table-column prop="title" label="标题" min-width="140" show-overflow-tooltip />
      <el-table-column prop="template_name" label="模板" min-width="140" show-overflow-tooltip />
      <el-table-column label="版式" width="72" align="center">
        <template slot-scope="{ row }">
          <span :class="row.has_layout ? 'tag-layout ok' : 'tag-layout'">{{ row.has_layout ? '已定稿' : '未定稿' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="creator_name" label="创建人" width="100" />
      <el-table-column prop="gxsj" label="更新时间" width="160" />
      <el-table-column label="操作" width="220" fixed="right">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="goEdit(row)">编辑</el-button>
          <el-button type="text" size="small" :loading="exportingId === row.id" @click="exportPdf(row)">导出 PDF</el-button>
          <el-button type="text" size="small" style="color:#f56c6c" @click="remove(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog title="新建合同" :visible.sync="showCreate" width="520px" @open="loadTemplates">
      <el-form label-width="96px" size="small" class="create-form">
        <el-form-item label="选择模板" required>
          <el-select v-model="createForm.template_id" filterable placeholder="请选择模板" style="width:100%">
            <el-option-group v-for="(items, cat) in templateGroups" :key="cat" :label="cat">
              <el-option
                v-for="t in items"
                :key="t.id"
                :label="t.name"
                :value="t.id"
              />
            </el-option-group>
          </el-select>
        </el-form-item>

        <el-alert
          class="layout-flow-alert"
          type="info"
          :closable="false"
          show-icon
          title="定稿版式流程"
        >
          <ol class="flow-steps">
            <li>创建后在 Word 中手写 <code v-pre>{{jiafang}}</code>、<code v-pre>{{address}}</code> 等变量</li>
            <li>保存 Word → 点击「注册版式」</li>
            <li>导出 PDF 时 POST 同名 JSON 字段（文字或图片 URL/路径）</li>
          </ol>
        </el-alert>

        <p class="form-footnote" v-pre>变量名与 Word 中 {{}} 完全一致；图片传 https:// 或 exports/… 路径，文字原样写入。</p>
      </el-form>
      <div slot="footer">
        <el-button @click="showCreate = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="submitCreate">创建并编辑 Word</el-button>
      </div>
    </el-dialog>

    <el-collapse v-if="apiHint" class="api-hint" value="">
      <el-collapse-item title="接口导出说明（模板定稿后）" name="api">
        <p class="api-line">按合同 ID 导出已保存的 Word：</p>
        <code class="api-code">GET yn_api.php?yn_contract_export_pdf=1&amp;id=合同ID&amp;yhid=…</code>
        <p class="api-line">定稿版式出 PDF — 可以这样 POST（字段名与 Word 里 <code v-pre>{{变量}}</code> 一致）：</p>
        <code class="api-code">POST yn_api.php?yn_contract_layout_pdf=1&amp;yhid=…</code>
        <pre class="api-pre">{
  "contract_no": "HT202607041533",
  "party_a": {
    "jiafang": "测试用户",
    "address": "测试地址",
    "jiafanggongzhang": "https://yf.yasenkj.cn/yasen/common/imgs/days/123.jpg",
    "jiafangqianming": "https://yf.yasenkj.cn/yasen/common/imgs/days/1781698582318.png"
  }
}</pre>
        <p class="api-line api-muted">contract_no 用列表里的合同编号；party_a 只是 JSON 容器名。中文没问题，UTF-8，Header 设 Content-Type: application/json。文字原样写入，图片传 https:// 或路径。</p>
        <p class="api-line" v-pre>Word 示例：甲方：{{jiafang}}　地址：{{address}}　公章：{{jiafanggongzhang}}　签名：{{jiafangqianming}}</p>
        <p class="api-line">注册定稿版式（Word 手写变量并保存后）：</p>
        <code class="api-code">POST yn_api.php?yn_contract_register_layout=1&amp;yhid=…</code>
        <pre class="api-pre">{"contract_no":"HT202607041533"}</pre>
        <p class="api-line api-muted">也可用 contract_id；不再需要用 template_id。列表「已定稿」表示该合同已注册版式。</p>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import api from '@/utils/api'
import { userParams } from '@/utils/user'

const API_BASE = process.env.VUE_APP_API_BASE || ''

export default {
  name: 'ContractList',
  data() {
    return {
      list: [],
      loading: false,
      showCreate: false,
      creating: false,
      exportingId: null,
      apiHint: true,
      createForm: { template_id: '' },
      templateGroups: {}
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await api.get('yn_contract_list')
        this.list = (res.data && res.data.list) || []
      } catch (e) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadTemplates() {
      try {
        const res = await api.get('yn_contract_templates')
        const data = res.data || {}
        if (!Object.keys(this.templateGroups).length) {
          this.templateGroups = data.categories || {}
        }
        if (!this.createForm.template_id) {
          const all = data.templates || []
          const preferred = all.find(t => t.name === '社区卫生服务中心1')
          if (preferred) this.createForm.template_id = preferred.id
        }
      } catch (e) {
        this.$message.error('模板加载失败')
      }
    },
    openCreate() {
      this.createForm = { template_id: this.createForm.template_id || '' }
      this.showCreate = true
    },
    async submitCreate() {
      if (!this.createForm.template_id) {
        this.$message.warning('请选择模板')
        return
      }
      this.creating = true
      try {
        const res = await api.postJson('yn_contract_create', {
          template_id: this.createForm.template_id
        })
        const data = res.data || {}
        if (data.error) {
          this.$message.error(data.error)
          return
        }
        this.showCreate = false
        this.$message.success('创建成功')
        this.$router.push('/contracts/' + data.id)
      } catch (e) {
        this.$message.error('创建失败')
      } finally {
        this.creating = false
      }
    },
    goEdit(row) {
      this.$router.push('/contracts/' + row.id)
    },
    async remove(row) {
      try {
        await this.$confirm('确定删除该合同？', '提示', { type: 'warning' })
      } catch (e) {
        return
      }
      try {
        const res = await api.post('yn_contract_delete', { id: row.id })
        if (res.data && res.data.error) {
          this.$message.error(res.data.error)
          return
        }
        this.$message.success('已删除')
        this.load()
      } catch (e) {
        this.$message.error('删除失败')
      }
    },
    async exportPdf(row) {
      this.exportingId = row.id
      try {
        const qs = new URLSearchParams(userParams({ yn_contract_export_pdf: '1', id: row.id }))
        const url = API_BASE + '/yn_api.php?' + qs.toString()
        const res = await fetch(url)
        if (!res.ok) throw new Error('HTTP ' + res.status)
        const ct = (res.headers.get('Content-Type') || '').toLowerCase()
        if (ct.includes('json') || ct.includes('text/plain')) {
          const text = await res.text()
          let msg = text
          try { msg = JSON.parse(text).error || text } catch (e) { /* keep */ }
          throw new Error(msg || '导出失败')
        }
        const blob = await res.blob()
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = (row.title || row.template_name || row.contract_no || 'contract') + '.pdf'
        a.click()
        URL.revokeObjectURL(a.href)
        this.$message.success('PDF 已下载')
      } catch (e) {
        this.$message.error(e.message || '导出失败')
      } finally {
        this.exportingId = null
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.page-header h2 {
  margin: 0;
  font-size: 18px;
}
.tag-layout {
  font-size: 12px;
  color: #909399;
}
.tag-layout.ok {
  color: #67c23a;
  font-weight: 500;
}
.layout-flow-alert {
  margin: 4px 0 14px;
}
.form-footnote {
  margin: 12px 0 0 96px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
.api-hint {
  margin-top: 20px;
  border: none;
}
.api-hint >>> .el-collapse-item__header {
  font-size: 13px;
  color: #606266;
  border: none;
  background: transparent;
}
.api-line {
  margin: 0 0 6px;
  font-size: 12px;
  color: #606266;
}
.api-line.api-muted {
  color: #909399;
}
.api-code, .api-pre {
  display: block;
  margin: 0 0 12px;
  padding: 10px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 12px;
  color: #303133;
  word-break: break-all;
  white-space: pre-wrap;
}
</style>
